<template>
  <div class="filters-section">
    <div class="search-box">
      <i class="fas fa-search search-icon"></i>
      <input 
        type="text" 
        :value="searchQuery" 
        @input="$emit('update:search-query', $event.target.value)"
        placeholder="Search instructors by name or expertise..."
        class="search-input"
      >
      <button 
        v-if="searchQuery" 
        @click="$emit('clear-search')" 
        class="clear-button"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="filters-row">
      <div class="filter-group">
        <label for="expertise-filter">Expertise</label>
        <select 
          id="expertise-filter" 
          :value="selectedExpertise" 
          @change="$emit('update:selected-expertise', $event.target.value)"
          class="filter-select"
        >
          <option value="">All Expertise</option>
          <option v-for="expertise in expertiseOptions" 
                  :key="expertise" 
                  :value="expertise">
            {{ expertise }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <label for="rating-filter">Minimum Rating</label>
        <select 
          id="rating-filter" 
          :value="selectedRating"
          @change="$emit('update:selected-rating', $event.target.value)"
          class="filter-select"
        >
          <option value="">Any Rating</option>
          <option value="4.5">4.5+ Stars</option>
          <option value="4.0">4.0+ Stars</option>
          <option value="3.5">3.5+ Stars</option>
          <option value="3.0">3.0+ Stars</option>
        </select>
      </div>
      <div class="filter-group">
        <label for="sort-by">Sort By</label>
        <select 
          id="sort-by" 
          :value="sortBy"
          @change="$emit('update:sort-by', $event.target.value)"
          class="filter-select"
        >
          <option value="name-asc">Name (A-Z)</option>
          <option value="name-desc">Name (Z-A)</option>
          <option value="rating-desc">Highest Rated</option>
          <option value="rating-asc">Lowest Rated</option>
          <option value="courses-desc">Most Courses</option>
          <option value="courses-asc">Least Courses</option>
        </select>
      </div>
      <div class="filter-group">
        <label for="language-filter">Language</label>
        <select 
          id="language-filter" 
          :value="selectedLanguage"
          @change="$emit('update:selected-language', $event.target.value)"
          class="filter-select"
        >
          <option value="">All Languages</option>
          <option v-for="language in languageOptions" 
                  :key="language" 
                  :value="language">
            {{ language }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <label for="availability-filter">Availability</label>
        <select 
          id="availability-filter" 
          :value="selectedAvailability"
          @change="$emit('update:selected-availability', $event.target.value)"
          class="filter-select"
        >
          <option value="">Any Availability</option>
          <option value="available">Available for Private Sessions</option>
          <option value="not-available">Group Sessions Only</option>
        </select>
      </div>
      <div class="filter-group">
        <label for="price-filter">Price Range</label>
        <select 
          id="price-filter" 
          :value="selectedPrice"
          @change="$emit('update:selected-price', $event.target.value)"
          class="filter-select"
        >
          <option value="">Any Price</option>
          <option value="economy">Economy ($)</option>
          <option value="standard">Standard ($$)</option>
          <option value="premium">Premium ($$$)</option>
        </select>
      </div>
      <div class="filter-group">
        <label for="experience-filter">Experience Level</label>
        <select 
          id="experience-filter" 
          :value="selectedExperience"
          @change="$emit('update:selected-experience', $event.target.value)"
          class="filter-select"
        >
          <option value="">Any Experience</option>
          <option value="beginner">Beginner-friendly</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
          <option value="all-levels">All Levels</option>
        </select>
      </div>
      <div class="filter-group">
        <label for="teaching-style-filter">Teaching Style</label>
        <select 
          id="teaching-style-filter" 
          :value="selectedTeachingStyle"
          @change="$emit('update:selected-teaching-style', $event.target.value)"
          class="filter-select"
        >
          <option value="">Any Teaching Style</option>
          <option value="practical">Practical/Hands-on</option>
          <option value="theoretical">Theoretical</option>
          <option value="interactive">Interactive</option>
          <option value="self-paced">Self-paced</option>
        </select>
      </div>
      <div class="filter-group">
        <label for="location-filter">Location Type</label>
        <select 
          id="location-filter" 
          :value="selectedLocation"
          @change="$emit('update:selected-location', $event.target.value)"
          class="filter-select"
        >
          <option value="">Any Location</option>
          <option value="online">Online Only</option>
          <option value="in-person">In-Person Only</option>
          <option value="hybrid">Hybrid</option>
        </select>
      </div>
      <button 
        @click="$emit('clear-filters')" 
        class="clear-filters-btn"
        v-if="hasActiveFilters"
      >
        <i class="fas fa-times"></i> Clear Filters
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FiltersSection',
  props: {
    searchQuery: {
      type: String,
      default: ''
    },
    selectedExpertise: {
      type: String,
      default: ''
    },
    selectedRating: {
      type: String,
      default: ''
    },
    selectedLanguage: {
      type: String,
      default: ''
    },
    selectedAvailability: {
      type: String,
      default: ''
    },
    selectedPrice: {
      type: String,
      default: ''
    },
    selectedExperience: {
      type: String,
      default: ''
    },
    selectedTeachingStyle: {
      type: String,
      default: ''
    },
    selectedLocation: {
      type: String,
      default: ''
    },
    sortBy: {
      type: String,
      default: 'name-asc'
    },
    hasActiveFilters: {
      type: Boolean,
      default: false
    },
    expertiseOptions: {
      type: Array,
      default: () => []
    },
    languageOptions: {
      type: Array,
      default: () => ['English', 'Spanish', 'French', 'German', 'Chinese', 'Arabic', 'Japanese']
    }
  },
  emits: [
    'update:search-query',
    'update:selected-expertise',
    'update:selected-rating',
    'update:selected-language',
    'update:selected-availability',
    'update:selected-price',
    'update:selected-experience',
    'update:selected-teaching-style',
    'update:selected-location',
    'update:sort-by',
    'clear-search',
    'clear-filters'
  ]
};
</script>

<style scoped>
.filters-section {
  max-width: 1200px;
  margin: 0 auto 2rem;
  background: var(--bg-white);
  border-radius: var(--radius-2xl);
  padding: 1.2rem 1.2rem 1.2rem 1.2rem;
  box-shadow: var(--shadow-lg);
  border: none;
  position: relative;
  overflow: hidden;
}

.filters-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 8px;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
}

.search-box {
  position: relative;
  margin-bottom: 1rem;
}

.search-input {
  width: 100%;
  padding: 1.25rem 1.25rem 1.25rem 3.5rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-xl);
  font-size: 1.1rem;
  color: var(--text-dark);
  background: var(--bg-white);
  transition: var(--transition);
  box-shadow: var(--shadow-sm);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 4px var(--primary-light);
}

.search-icon {
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--primary-color);
  font-size: 1.25rem;
}

.clear-button {
  position: absolute;
  right: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  background: var(--primary-light);
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: var(--transition);
}

.clear-button:hover {
  background: var(--primary-color);
  color: var(--bg-white);
}

.filters-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.filter-group label {
  font-size: 0.95rem;
  color: var(--text-medium);
  font-weight: 600;
  margin-left: 0.5rem;
}

.filter-select {
  width: 100%;
  padding: 0.6rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-xl);
  background-color: var(--bg-white);
  color: var(--text-dark);
  font-size: 0.95rem;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%236366f1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1.2em;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 4px var(--primary-light);
}

.clear-filters-btn {
  padding: 0.6rem 1.2rem;
  background: var(--primary-light);
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  border-radius: var(--radius-xl);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: var(--transition);
  margin-top: auto;
  font-size: 0.95rem;
}

.clear-filters-btn:hover {
  background: var(--primary-color);
  color: var(--bg-white);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

@media (max-width: 1200px) {
  .filters-section {
    padding: 2rem;
  }
}

@media (max-width: 992px) {
  .filters-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .filters-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .filters-section {
    padding: 1.5rem;
  }
}
</style> 